import React, { useEffect, useState } from "react";
import { Plus, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";

const STATUSES = [
  { key: "nuova", label: "🟢 Nuova", color: "bg-emerald-100 text-emerald-700" },
  { key: "contattare", label: "🟡 Contattare", color: "bg-amber-100 text-amber-700" },
  { key: "documenti", label: "🟠 Documenti", color: "bg-orange-100 text-orange-700" },
  { key: "proposta", label: "🔴 Proposta", color: "bg-rose-100 text-rose-700" },
  { key: "attesa", label: "⚫ Attesa", color: "bg-slate-200 text-slate-700" },
  { key: "done", label: "✅ Fatto", color: "bg-blue-100 text-blue-700" },
];

const PRIORITY = { low: { label: "Bassa", color: "bg-slate-100 text-slate-600" }, medium: { label: "Media", color: "bg-blue-100 text-blue-700" }, high: { label: "Alta", color: "bg-rose-100 text-rose-700" } };

export default function TasksBoard({ propertyId }) {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "nuova", priority: "medium", due_date: "" });

  const load = async () => {
    const params = propertyId ? { property_id: propertyId } : {};
    const { data } = await api.get("/tasks", { params });
    setTasks(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const create = async () => {
    try {
      await api.post("/tasks", { ...form, property_id: propertyId || null, due_date: form.due_date ? new Date(form.due_date).toISOString() : null });
      toast.success("Attività creata"); setOpen(false); setForm({ title: "", description: "", status: "nuova", priority: "medium", due_date: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const changeStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await api.delete(`/tasks/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const overdue = (t) => t.due_date && t.status !== "done" && new Date(t.due_date) < new Date();

  return (
    <div className="space-y-4" data-testid="tasks-board">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{tasks.length} attività</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button size="sm" onClick={() => setOpen(true)} className="gap-2" data-testid="new-task-button"><Plus className="w-4 h-4" /> Nuova attività</Button>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuova attività</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Titolo *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
              <div><Label>Descrizione</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Stato</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Priorità</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Bassa</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Scadenza</Label><Input type="datetime-local" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={create} disabled={!form.title} data-testid="task-submit-button">Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {tasks.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6">Nessuna attività. Crea la prima.</div>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => {
            const status = STATUSES.find((s) => s.key === t.status) || STATUSES[0];
            return (
              <div key={t.id} className={`p-3 rounded-md border ${overdue(t) ? "border-rose-300 bg-rose-50/40 dark:bg-rose-950/20" : "border-border/60"}`} data-testid={`task-${t.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{t.title}</span>
                      <Badge className={`${PRIORITY[t.priority]?.color} border-transparent text-[10px]`}>{PRIORITY[t.priority]?.label}</Badge>
                      {overdue(t) && <Badge className="bg-rose-500 text-white border-transparent text-[10px]">Scaduta</Badge>}
                    </div>
                    {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                    {t.due_date && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(t.due_date).toLocaleString("it-IT")}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select value={t.status} onValueChange={(v) => changeStatus(t.id, v)}>
                      <SelectTrigger className={`h-7 text-xs w-32 ${status.color} border-transparent`} data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => remove(t.id)} className="h-7 w-7" data-testid={`del-task-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
